import React, { useCallback, useEffect, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  CornerDownRight,
  MessageSquare,
  Pencil,
  Reply,
  Trash2,
} from 'lucide-react';

import { getUserInfo } from '../../utils/auth';
import { formatDateTimeFromApi } from '../../utils/dateTime';
import {
  createComment,
  deleteComment,
  getCommentList,
  getCommentThread,
  updateComment,
  type BoardComment,
} from '../../utils/boardComment';

const COMMENT_PAGE_SIZE = 20;
const COMMENT_BASE_PADDING_PX = 16;
const COMMENT_INDENT_PX = 16;
const MAX_COMMENT_INDENT_DEPTH = 5;

/** 게시물 스레드 목록과 동일: depth 기준 들여쓰기 */
function getCommentPaddingLeft(depth?: number): number {
  const d = depth ?? 0;
  return COMMENT_BASE_PADDING_PX + Math.min(d, MAX_COMMENT_INDENT_DEPTH) * COMMENT_INDENT_PX;
}

interface PostCommentSectionProps {
  projectCode: string;
  boardCode: string;
  postCode: string;
  commentCount: number;
  onCommentCountChange?: (count: number) => void;
}

function canManageComment(comment: BoardComment): boolean {
  const user = getUserInfo();
  if (!user || !comment.createdBy) return false;
  const author = comment.createdBy;
  return (
    author === user.userId ||
    author === user.loginId ||
    author === user.accountCode
  );
}

const PostCommentSection: React.FC<PostCommentSectionProps> = ({
  projectCode,
  boardCode,
  postCode,
  commentCount,
  onCommentCountChange,
}) => {
  const [rootComments, setRootComments] = useState<BoardComment[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [hasNext, setHasNext] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [expandedCodes, setExpandedCodes] = useState<Set<string>>(new Set());
  const [threadMap, setThreadMap] = useState<Record<string, BoardComment[]>>({});
  const [threadLoading, setThreadLoading] = useState<Set<string>>(new Set());
  const [threadError, setThreadError] = useState<Record<string, string>>({});

  const [rootText, setRootText] = useState('');
  const [rootError, setRootError] = useState<string | null>(null);
  const [isRootSubmitting, setIsRootSubmitting] = useState(false);

  const [replyToCode, setReplyToCode] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyError, setReplyError] = useState<string | null>(null);
  const [isReplySubmitting, setIsReplySubmitting] = useState(false);

  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  const [deletingCode, setDeletingCode] = useState<string | null>(null);

  const fetchRootComments = useCallback(
    async (cursor?: string, append = false) => {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setListError(null);
      }

      try {
        const res = await getCommentList(projectCode, boardCode, postCode, {
          cursor,
          size: COMMENT_PAGE_SIZE,
          activeOnly: false,
        });
        setRootComments((prev) => (append ? [...prev, ...res.comments] : res.comments));
        setNextCursor(res.nextCursor);
        setHasNext(res.hasNext);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : '댓글 목록을 불러오지 못했습니다.';
        if (!append) {
          setListError(message);
          setRootComments([]);
          setNextCursor(undefined);
          setHasNext(false);
        } else {
          alert(message);
        }
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [projectCode, boardCode, postCode]
  );

  useEffect(() => {
    setRootComments([]);
    setNextCursor(undefined);
    setHasNext(false);
    setExpandedCodes(new Set());
    setThreadMap({});
    setThreadLoading(new Set());
    setThreadError({});
    setReplyToCode(null);
    setReplyText('');
    setEditingCode(null);
    fetchRootComments();
  }, [fetchRootComments]);

  const loadThread = async (commentCode: string) => {
    setThreadLoading((prev) => new Set(prev).add(commentCode));
    setThreadError((prev) => {
      const next = { ...prev };
      delete next[commentCode];
      return next;
    });

    try {
      const res = await getCommentThread(projectCode, boardCode, postCode, commentCode);
      // 스레드 API는 앵커(원댓글)를 포함할 수 있음 — 목록에 이미 보이므로 자식만 표시
      const descendants = res.comments.filter((c) => c.code !== commentCode);
      setThreadMap((prev) => ({ ...prev, [commentCode]: descendants }));
    } catch (err) {
      setThreadError((prev) => ({
        ...prev,
        [commentCode]:
          err instanceof Error ? err.message : '답글을 불러오지 못했습니다.',
      }));
    } finally {
      setThreadLoading((prev) => {
        const next = new Set(prev);
        next.delete(commentCode);
        return next;
      });
    }
  };

  const toggleThread = async (comment: BoardComment) => {
    const code = comment.code;
    if (expandedCodes.has(code)) {
      setExpandedCodes((prev) => {
        const next = new Set(prev);
        next.delete(code);
        return next;
      });
      return;
    }

    setExpandedCodes((prev) => new Set(prev).add(code));
    if (!threadMap[code]) {
      await loadThread(code);
    }
  };

  const handleRootSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = rootText.trim();
    if (!trimmed) {
      setRootError('댓글 내용을 입력해주세요.');
      return;
    }

    setRootError(null);
    setIsRootSubmitting(true);
    try {
      await createComment(projectCode, boardCode, postCode, { content: trimmed });
      setRootText('');
      onCommentCountChange?.(commentCount + 1);
      await fetchRootComments();
    } catch (err) {
      setRootError(err instanceof Error ? err.message : '댓글 등록에 실패했습니다.');
    } finally {
      setIsRootSubmitting(false);
    }
  };

  const handleReplySubmit = async (
    e: React.FormEvent,
    parentCode: string,
    threadRootCode: string
  ) => {
    e.preventDefault();
    const trimmed = replyText.trim();
    if (!trimmed) {
      setReplyError('댓글 내용을 입력해주세요.');
      return;
    }

    setReplyError(null);
    setIsReplySubmitting(true);
    try {
      await createComment(projectCode, boardCode, postCode, {
        content: trimmed,
        parentCode,
      });
      setReplyText('');
      setReplyToCode(null);
      onCommentCountChange?.(commentCount + 1);

      const rootComment = rootComments.find((c) => c.code === threadRootCode);
      if (rootComment) {
        setRootComments((prev) =>
          prev.map((c) =>
            c.code === threadRootCode ? { ...c, replyCount: c.replyCount + 1 } : c
          )
        );
      }

      if (expandedCodes.has(threadRootCode)) {
        await loadThread(threadRootCode);
      }
    } catch (err) {
      setReplyError(err instanceof Error ? err.message : '답글 등록에 실패했습니다.');
    } finally {
      setIsReplySubmitting(false);
    }
  };

  const startEdit = (comment: BoardComment) => {
    setEditingCode(comment.code);
    setEditText(comment.content);
    setEditError(null);
    setReplyToCode(null);
  };

  const cancelEdit = () => {
    setEditingCode(null);
    setEditText('');
    setEditError(null);
  };

  const handleEditSubmit = async (e: React.FormEvent, commentCode: string) => {
    e.preventDefault();
    const trimmed = editText.trim();
    if (!trimmed) {
      setEditError('댓글 내용을 입력해주세요.');
      return;
    }

    setEditError(null);
    setIsEditSubmitting(true);
    try {
      const updated = await updateComment(
        projectCode,
        boardCode,
        postCode,
        commentCode,
        { content: trimmed }
      );
      const patch = (c: BoardComment) => (c.code === commentCode ? updated : c);
      setRootComments((prev) => prev.map(patch));
      setThreadMap((prev) => {
        const next: Record<string, BoardComment[]> = {};
        for (const [key, list] of Object.entries(prev)) {
          next[key] = list.map(patch);
        }
        return next;
      });
      cancelEdit();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : '댓글 수정에 실패했습니다.');
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleDelete = async (comment: BoardComment, isChild: boolean) => {
    if (!window.confirm('이 댓글을 삭제하시겠습니까?')) return;

    setDeletingCode(comment.code);
    try {
      await deleteComment(projectCode, boardCode, postCode, comment.code);
      onCommentCountChange?.(Math.max(0, commentCount - 1));

      if (isChild) {
        setThreadMap((prev) => {
          const next = { ...prev };
          for (const [parentCode, children] of Object.entries(next)) {
            if (children.some((c) => c.code === comment.code)) {
              next[parentCode] = children.filter((c) => c.code !== comment.code);
              setRootComments((roots) =>
                roots.map((r) =>
                  r.code === parentCode
                    ? { ...r, replyCount: Math.max(0, r.replyCount - 1) }
                    : r
                )
              );
            }
          }
          return next;
        });
      } else {
        setRootComments((prev) => prev.filter((c) => c.code !== comment.code));
        setExpandedCodes((prev) => {
          const next = new Set(prev);
          next.delete(comment.code);
          return next;
        });
        setThreadMap((prev) => {
          const next = { ...prev };
          delete next[comment.code];
          return next;
        });
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : '댓글 삭제에 실패했습니다.');
    } finally {
      setDeletingCode(null);
    }
  };

  const handleLoadMore = () => {
    if (!hasNext || !nextCursor || isLoadingMore) return;
    fetchRootComments(nextCursor, true);
  };

  const renderCommentForm = (
    value: string,
    onChange: (v: string) => void,
    onSubmit: (e: React.FormEvent) => void,
    error: string | null,
    isSubmitting: boolean,
    placeholder: string,
    onCancel?: () => void
  ) => (
    <form onSubmit={onSubmit} className="mt-3 space-y-2">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={2}
        className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
      />
      {error && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            취소
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {isSubmitting ? '등록 중...' : '등록'}
        </button>
      </div>
    </form>
  );

  const renderCommentRow = (
    comment: BoardComment,
    isChild: boolean,
    threadRootCode: string
  ) => {
    const depth = comment.depth ?? 0;
    const paddingLeft = getCommentPaddingLeft(depth);
    const showReplyIcon = depth > 0;
    const isEditing = editingCode === comment.code;
    const isReplying = replyToCode === comment.code;
    const canManage = canManageComment(comment);
    const hasReplies = comment.replyCount > 0;
    const isExpanded = expandedCodes.has(comment.code);
    const children = threadMap[comment.code] ?? [];
    const isThreadLoading = threadLoading.has(comment.code);

    return (
      <div
        key={comment.code}
        className={`py-4 ${isChild ? 'border-t border-gray-50' : 'border-b border-gray-100 last:border-b-0'}`}
        style={{ paddingLeft: `${paddingLeft}px`, paddingRight: `${COMMENT_BASE_PADDING_PX}px` }}
      >
        {isEditing ? (
          <form onSubmit={(e) => handleEditSubmit(e, comment.code)} className="space-y-2">
            <textarea
              value={editText}
              onChange={(e) => {
                setEditText(e.target.value);
                setEditError(null);
              }}
              rows={3}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
            />
            {editError && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {editError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelEdit}
                className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isEditSubmitting}
                className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {isEditSubmitting ? '저장 중...' : '저장'}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="flex items-start gap-2">
              {showReplyIcon && (
                <CornerDownRight className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                  {comment.content}
                </p>
                <p className="mt-2 text-xs text-gray-500">
                  {comment.createdBy && <span className="mr-2">{comment.createdBy}</span>}
                  <span>{formatDateTimeFromApi(comment.createdAt)}</span>
                  {comment.updatedAt && comment.updatedAt !== comment.createdAt && (
                    <span className="ml-2 text-gray-400">(수정됨)</span>
                  )}
                </p>
              </div>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
              <button
                type="button"
                onClick={() => {
                  setReplyToCode(isReplying ? null : comment.code);
                  setReplyText('');
                  setReplyError(null);
                  setEditingCode(null);
                }}
                className="inline-flex items-center gap-1 text-gray-600 hover:text-blue-600"
              >
                <Reply className="w-3.5 h-3.5" />
                답글
              </button>
              {canManage && (
                <>
                  <button
                    type="button"
                    onClick={() => startEdit(comment)}
                    className="inline-flex items-center gap-1 text-gray-600 hover:text-blue-600"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    수정
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(comment, isChild)}
                    disabled={deletingCode === comment.code}
                    className="inline-flex items-center gap-1 text-gray-600 hover:text-red-600 disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    삭제
                  </button>
                </>
              )}
              {!isChild && hasReplies && (
                <button
                  type="button"
                  onClick={() => toggleThread(comment)}
                  disabled={isThreadLoading}
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                >
                  {isExpanded ? (
                    <ChevronUp className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5" />
                  )}
                  {isThreadLoading
                    ? '불러오는 중...'
                    : isExpanded
                      ? '답글 숨기기'
                      : `답글 ${comment.replyCount}개 보기`}
                </button>
              )}
            </div>

            {isReplying &&
              renderCommentForm(
                replyText,
                (v) => {
                  setReplyText(v);
                  setReplyError(null);
                },
                (e) => handleReplySubmit(e, comment.code, threadRootCode),
                replyError,
                isReplySubmitting,
                '답글을 입력하세요',
                () => {
                  setReplyToCode(null);
                  setReplyText('');
                  setReplyError(null);
                }
              )}

            {!isChild && isExpanded && (
              <div className="mt-1 border-t border-gray-50">
                {threadError[comment.code] && (
                  <p
                    className="py-2 text-sm text-red-600"
                    style={{
                      paddingLeft: `${paddingLeft}px`,
                      paddingRight: `${COMMENT_BASE_PADDING_PX}px`,
                    }}
                  >
                    {threadError[comment.code]}
                  </p>
                )}
                {children.length === 0 && !isThreadLoading && !threadError[comment.code] && (
                  <p
                    className="py-2 text-sm text-gray-500"
                    style={{
                      paddingLeft: `${getCommentPaddingLeft(1)}px`,
                      paddingRight: `${COMMENT_BASE_PADDING_PX}px`,
                    }}
                  >
                    답글이 없습니다.
                  </p>
                )}
                {children.map((child) => renderCommentRow(child, true, threadRootCode))}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <section className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 lg:px-6 py-3 border-b border-gray-200 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-900">댓글 작성</h3>
        </div>
        <div className="px-4 lg:px-6 py-4">
          <form onSubmit={handleRootSubmit} className="space-y-3">
            <textarea
              value={rootText}
              onChange={(e) => {
                setRootText(e.target.value);
                setRootError(null);
              }}
              placeholder="댓글을 입력하세요"
              rows={3}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
            />
            {rootError && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {rootError}
              </p>
            )}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isRootSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {isRootSubmitting ? '등록 중...' : '댓글 등록'}
              </button>
            </div>
          </form>
        </div>
      </section>

      <section className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 lg:px-6 py-3 border-b border-gray-200 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-900">댓글</h3>
          {commentCount > 0 && (
            <span className="text-xs text-gray-500">({commentCount})</span>
          )}
        </div>

        {listError && (
          <div className="px-4 lg:px-6 py-4 text-sm text-red-600">
            {listError}
            <button
              type="button"
              onClick={() => fetchRootComments()}
              className="ml-2 underline font-medium"
            >
              다시 시도
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600" />
          </div>
        ) : rootComments.length === 0 && !listError ? (
          <p className="px-4 lg:px-6 py-8 text-sm text-gray-500 text-center">
            등록된 댓글이 없습니다.
          </p>
        ) : (
          <div>
            {rootComments.map((comment) => renderCommentRow(comment, false, comment.code))}
            {hasNext && (
              <div className="px-4 py-4 border-t border-gray-200 text-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  {isLoadingMore ? '불러오는 중...' : '더보기'}
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </>
  );
};

export default PostCommentSection;